import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { FiAlertOctagon, FiAlertTriangle, FiArrowRight } from "react-icons/fi";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n, type Key } from "@/lib/i18n";
import { confidence, displayName, type IntegrationRow, type Note } from "@/lib/integrations";
import { durText } from "@/components/OntStatus";
import { cn } from "@/lib/utils";

export type IntegrationsResponse = { integrations: IntegrationRow[]; generated_at: string };

/** One cached poll of /integrations for the page, the Overview and the banner. */
export function useIntegrations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["integrations"],
    queryFn: () => api<IntegrationsResponse>("/api/v1/integrations"),
    refetchInterval: 30_000,
    enabled: !!user && user.role !== "portviewer",
  });
}

/** 1s clock for relative ages. */
export function useNow(stepMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), stepMs);
    return () => clearInterval(id);
  }, [stepMs]);
  return now;
}

export function noteText(n: Note, t: (k: Key, p?: Record<string, string | number>) => string) {
  return t(n.key as Key, { ...n.params, name: displayName(n.name), age: durText(Number(n.params.age ?? 0)) });
}

/**
 * The monitoring-failure warning every page carries: when the collectors that
 * describe the network are stale or failing, a dark OLT on screen may be old
 * data, not a dead OLT. Silent when confidence is good.
 */
export default function MonitoringBanner({ compact }: { compact?: boolean }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const q = useIntegrations();
  const now = useNow(5000);
  if (!q.data || pathname.startsWith("/integrations")) return null;
  const c = confidence(q.data.integrations, now);
  if (c.level === "good") return null;
  const low = c.level === "low";
  const Icon = low ? FiAlertOctagon : FiAlertTriangle;
  return (
    <div role="status" className={cn("flex items-center gap-3 border-b px-5",
      compact ? "py-1 text-[12px]" : "py-2 text-[13px]",
      low ? "border-signal-crit/50 bg-signal-crit/15 text-rose-100" : "border-amber-400/40 bg-amber-400/10 text-amber-100")}>
      <Icon className={cn("size-4 shrink-0", low ? "text-signal-crit" : "text-signal-warn")} />
      <span className="font-semibold tracking-[0.14em] uppercase">{t(low ? "int.posture.failure" : "int.posture.degraded")}</span>
      <span className="min-w-0 truncate">{c.notes.map((n) => noteText(n, t)).join("  ·  ")}</span>
      {low && <span className="hidden shrink-0 font-medium xl:inline">{t("int.banner.notDown")}</span>}
      <Link to="/integrations" className="ml-auto inline-flex shrink-0 items-center gap-1 font-medium hover:underline">
        {t("int.banner.investigate")} <FiArrowRight />
      </Link>
    </div>
  );
}
